import { Trans, useLingui } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Divider } from '@startuptools/ui/Divider';
import { Switch } from '@startuptools/ui/Switch';
import { type GqlCapTable, GqlCurrency } from '../../../graphql/base-types.graphql';
import { Controller, useForm } from 'react-hook-form';
import { type InvestmentRound, type InvestmentRoundCalculatedValues } from './types';
import { BigNumber } from 'bignumber.js';
import React from 'react';

export const AddInvestment = ({
  onSubmit,
  capTable,
}: {
  onSubmit: (data: InvestmentRound) => void;
  capTable: Pick<GqlCapTable, 'currentShareCount' | 'pricePerShare'>;
}) => {
  const [didAdjustValue, setDidAdjustValue] = React.useState(false);
  const defaultPricePerShare = capTable.pricePerShare ?? '';
  const defaultPreMoneyValuation = capTable.pricePerShare
    ? new BigNumber(capTable.pricePerShare).times(capTable.currentShareCount).toString()
    : '';
  const { control, handleSubmit, watch, setValue } = useForm<InvestmentRound & InvestmentRoundCalculatedValues>({
    defaultValues: {
      name: '',
      pricePerShare: defaultPricePerShare,
      preMoneyValuation: defaultPreMoneyValuation,
      postMoneyValuation: defaultPreMoneyValuation,
      amount: '',
      count: '',
    },
  });

  const { i18n } = useLingui();

  const { pricePerShare, amount, preMoneyValuation } = watch();

  const [calculateFrom, setCalculateFrom] = React.useState<'pricePerShare' | 'valuation'>('pricePerShare');

  const setValuesBasedOnValuation = () => {
    if (!pricePerShare) return;
    const postMoneyValuation = new BigNumber(preMoneyValuation).plus(amount || '0');
    const sharePrice = new BigNumber(preMoneyValuation).dividedBy(capTable.currentShareCount);
    setValue('pricePerShare', sharePrice.toString());
    if (!amount) return;
    const shares = new BigNumber(amount).dividedBy(sharePrice).integerValue(BigNumber.ROUND_FLOOR);
    const adjustedPreMoneyValuation = new BigNumber(sharePrice).times(shares);
    setDidAdjustValue(!adjustedPreMoneyValuation.isEqualTo(preMoneyValuation));
    setValue('preMoneyValuation', adjustedPreMoneyValuation.toString());
    setValue('count', shares.toString());
    setValue('postMoneyValuation', postMoneyValuation.toString());
  };

  const setValuesBasedOnSharePrice = () => {
    if (!pricePerShare) return;
    const newShareCount = new BigNumber(amount).dividedBy(pricePerShare).integerValue(BigNumber.ROUND_FLOOR);
    const preMoneyValuation = new BigNumber(pricePerShare).times(capTable.currentShareCount);

    setValue('count', newShareCount.toString());
    setValue('preMoneyValuation', preMoneyValuation.toString());

    if (!amount) {
      setValue('postMoneyValuation', preMoneyValuation.toString());
      return;
    }

    const amountToRaise = newShareCount.times(pricePerShare);
    const postMoneyValuation = new BigNumber(amountToRaise).plus(preMoneyValuation);

    setDidAdjustValue(!amountToRaise.isEqualTo(amount));
    setValue('postMoneyValuation', postMoneyValuation.toString());
    setValue('amount', amountToRaise.toString());
  };

  const setCalculatedValues =
    calculateFrom === 'pricePerShare' ? setValuesBasedOnSharePrice : setValuesBasedOnValuation;

  const pricePerShareField = (
    <Controller
      key="pricePerShare"
      control={control}
      name="pricePerShare"
      rules={{ required: true }}
      render={({ field: { onChange, ...field }, formState }) => (
        <FormControl error={Boolean(formState.errors?.pricePerShare)}>
          <FormLabel>
            <Trans id="Share price" />
          </FormLabel>
          <NumericFormatInput
            {...field}
            onChange={onChange}
            onBlur={setCalculatedValues}
            lang={i18n.locale}
            endDecorator={<Typography sx={{ color: 'inherit' }}>{GqlCurrency.Sek}</Typography>}
            disabled={calculateFrom !== 'pricePerShare'}
          />
        </FormControl>
      )}
    />
  );

  const preMoneyValuationField = (
    <Controller
      control={control}
      name="preMoneyValuation"
      rules={{ required: true }}
      render={({ field, formState }) => (
        <FormControl error={Boolean(formState.errors?.preMoneyValuation)}>
          <FormLabel>
            <Trans id="Pre-money valuation" />
          </FormLabel>
          {calculateFrom === 'valuation' && didAdjustValue && (
            <FormHelperText>
              <Trans id="Adjusted value to ensure even share count." />
            </FormHelperText>
          )}
          <NumericFormatInput
            {...field}
            lang={i18n.locale}
            endDecorator={<Typography sx={{ color: 'inherit' }}>{GqlCurrency.Sek}</Typography>}
            disabled={calculateFrom !== 'valuation'}
            onBlur={setCalculatedValues}
          />
        </FormControl>
      )}
    />
  );

  return (
    <Stack direction="row">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <FormControl>
            <FormLabel>
              <Trans id="Calculate based on..." />
            </FormLabel>
            <Switch
              color="primary"
              startDecorator={
                <Typography level="body-sm">
                  <Trans id="Share price" />
                </Typography>
              }
              endDecorator={
                <Typography level="body-sm">
                  <Trans id="Valuation" />
                </Typography>
              }
              checked={calculateFrom === 'valuation'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCalculateFrom(event.target.checked ? 'valuation' : 'pricePerShare')
              }
            />
          </FormControl>

          <Controller
            control={control}
            name="name"
            rules={{ required: true }}
            render={({ field, formState }) => (
              <FormControl error={Boolean(formState.errors?.name)}>
                <FormLabel>
                  <Trans id="Name" />
                </FormLabel>
                <Input {...field} placeholder={i18n._('E.g. Series C')} />
              </FormControl>
            )}
          />
          {calculateFrom === 'pricePerShare' ? pricePerShareField : preMoneyValuationField}
          <Controller
            control={control}
            name="amount"
            rules={{ required: true }}
            render={({ field: { onChange, ...field }, formState }) => (
              <FormControl error={Boolean(formState.errors?.amount)}>
                <FormLabel>
                  <Trans id="Amount to raise" />
                </FormLabel>
                {calculateFrom === 'pricePerShare' && didAdjustValue && (
                  <FormHelperText>
                    <Trans id="Adjusted value to ensure even share count." />
                  </FormHelperText>
                )}
                <NumericFormatInput
                  {...field}
                  onChange={onChange}
                  onBlur={setCalculatedValues}
                  lang={i18n.locale}
                  endDecorator={<Typography sx={{ color: 'inherit' }}>{GqlCurrency.Sek}</Typography>}
                />
              </FormControl>
            )}
          />
          <Divider />
          <Typography level="body-sm">
            <Trans id="The following values are calculated automatically" />
          </Typography>

          {calculateFrom === 'pricePerShare' ? preMoneyValuationField : pricePerShareField}
          <Controller
            control={control}
            name="postMoneyValuation"
            rules={{ required: true }}
            render={({ field, formState }) => (
              <FormControl error={Boolean(formState.errors?.postMoneyValuation)}>
                <FormLabel>
                  <Trans id="Post-money valuation" />
                </FormLabel>
                <NumericFormatInput
                  {...field}
                  lang={i18n.locale}
                  endDecorator={<Typography sx={{ color: 'inherit' }}>{GqlCurrency.Sek}</Typography>}
                  disabled
                />
              </FormControl>
            )}
          />

          <FormControl>
            <FormLabel>
              <Trans id="Pre-money shares" />
            </FormLabel>
            <NumericFormatInput value={capTable.currentShareCount} readOnly disabled lang={i18n.locale} />
          </FormControl>

          <Controller
            control={control}
            name="count"
            rules={{
              required: true,
              validate: {
                isPositive: value => new BigNumber(value).isGreaterThan(0) || 'Value must be positive',
                isInteger: value => Number.isInteger(Number(value)) || 'Value must be an integer',
              },
            }}
            render={({ field, formState }) => (
              <FormControl error={Boolean(formState.errors?.count)}>
                <FormLabel>
                  <Trans id="New Shares (amount)" />
                </FormLabel>
                <NumericFormatInput {...field} readOnly disabled lang={i18n.locale} />
              </FormControl>
            )}
          />
          <Box>
            <Button type="submit">
              <Trans id="Save and Continue" />
            </Button>
          </Box>
        </Stack>
      </form>
    </Stack>
  );
};
